import { useEffect, useState } from 'react';
import {
  IconPlus, IconPencil, IconAlertCircle, IconCheck, IconPhoto,
  IconBan, IconCircleCheck, IconLink, IconArrowUp, IconArrowDown,
  IconCloudUpload,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import { pickImageViaCloudinary } from '../../lib/cloudinary';

interface ApiBanner {
  id: string;
  imageUrl: string;
  title: string | null;
  subtitle: string | null;
  linkUrl: string | null;
  linkProductId: string | null;
  linkCategoryId: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface ApiProduct  { id: string; name: string; categoryId: string; isActive: boolean }
interface ApiCategory { id: string; name: string; isActive: boolean }

type LinkType = 'none' | 'product' | 'category' | 'url';

export default function AdminBannersPage() {
  const [banners, setBanners]       = useState<ApiBanner[]>([]);
  const [products, setProducts]     = useState<ApiProduct[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [editing, setEditing]       = useState<ApiBanner | 'new' | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [b, p, c] = await Promise.all([
        fetch('/api/banners?includeInactive=1',  { credentials: 'include' }),
        fetch('/api/products',                   { credentials: 'include' }),
        fetch('/api/categories',                 { credentials: 'include' }),
      ]);
      if (!b.ok || !p.ok || !c.ok) throw new Error(`HTTP ${b.status}/${p.status}/${c.status}`);
      const bd = await b.json();
      const pd = await p.json();
      const cd = await c.json();
      setBanners(bd.banners ?? []);
      setProducts((pd.products ?? []).filter((x: ApiProduct) => x.isActive));
      setCategories((cd.categories ?? []).filter((x: ApiCategory) => x.isActive));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function move(index: number, delta: -1 | 1) {
    const next = [...banners];
    const swap = index + delta;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    setBanners(next);
    try {
      const r = await fetch('/api/banners/reorder', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: next.map((b) => b.id) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      setError((err as Error).message);
      void load();
    }
  }

  async function toggleActive(b: ApiBanner) {
    const next = !b.isActive;
    setBanners((p) => p.map((x) => x.id === b.id ? { ...x, isActive: next } : x));
    try {
      const r = await fetch(`/api/banners/${b.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      setBanners((p) => p.map((x) => x.id === b.id ? { ...x, isActive: !next } : x));
      setError((err as Error).message);
    }
  }

  const activeCount = banners.filter((b) => b.isActive).length;

  return (
    <AdminLayout
      title="Banners"
      subtitle={`${activeCount} active · ${banners.length} total · shown on the customer-facing /t/:code menu`}
      actions={
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
        >
          <IconPlus size={16} stroke={2.5} />
          New banner
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {/* Helper card */}
      <section className="rounded-lg bg-info-soft border border-info/30 p-4 mb-6">
        <div className="flex items-start gap-3">
          <IconPhoto size={22} className="text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-14 font-bold text-heading">How banners work</p>
            <p className="text-13 text-body mt-1 leading-snug">
              Banners appear in a swipeable carousel above the menu on
              <span className="font-mono mx-1">/t/:code</span> pages.
              Use them for daily specials, new items, or promotions.
              Customers see roughly two at a time with a peek of the third.
            </p>
            <p className="text-13 text-body mt-2 leading-snug">
              <strong>Image format:</strong> use a <strong>1080 × 1080 px</strong>
              (1:1 square) image so it crops cleanly. The uploader has
              a built-in cropper that enforces this.
            </p>
          </div>
        </div>
      </section>

      {loading ? (
        <p className="text-13 italic text-gray-700">Loading…</p>
      ) : banners.length === 0 ? (
        <section className="rounded-lg bg-surface border border-border p-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
            <IconPhoto size={22} stroke={2} />
          </div>
          <p className="text-14 font-bold text-heading">No banners yet</p>
          <p className="text-13 font-medium text-gray-700 mt-1 mb-4">Add your first one to show specials on the customer menu page.</p>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
          >
            <IconPlus size={16} stroke={2.5} />
            Add banner
          </button>
        </section>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((b, idx) => (
            <li key={b.id} className={`rounded-lg overflow-hidden border-2 ${b.isActive ? 'border-border' : 'border-dashed border-border opacity-60'} bg-surface shadow-sm`}>
              <div className="aspect-square w-full bg-light overflow-hidden relative">
                <img src={b.imageUrl} alt={b.title ?? 'Banner'} className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 inline-flex items-center px-2 h-6 rounded-md bg-[rgba(0,0,0,0.7)] text-[#FFFFFF] text-11 font-bold tabular-nums">
                  #{idx + 1}
                </span>
                {!b.isActive && (
                  <span className="absolute top-2 right-2 inline-flex items-center px-2 h-6 rounded-md bg-light border border-border text-11 font-bold uppercase tracking-wider text-gray-700">
                    Inactive
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-14 font-bold text-heading truncate">{b.title ?? <span className="italic text-gray-600">No title</span>}</p>
                {b.subtitle && <p className="text-12 font-medium text-gray-700 truncate mt-0.5">{b.subtitle}</p>}
                {/* Link target chip */}
                <div className="mt-2">
                  {b.linkProductId ? (
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded bg-primary-soft text-primary text-11 font-bold uppercase tracking-wider">
                      → {products.find((p) => p.id === b.linkProductId)?.name ?? 'Product'}
                    </span>
                  ) : b.linkCategoryId ? (
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded bg-success-soft text-success text-11 font-bold uppercase tracking-wider">
                      → {categories.find((c) => c.id === b.linkCategoryId)?.name ?? 'Category'}
                    </span>
                  ) : b.linkUrl ? (
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded bg-info-soft text-info text-11 font-bold uppercase tracking-wider">
                      → External link
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded bg-light border border-border text-gray-700 text-11 font-bold uppercase tracking-wider">
                      No link
                    </span>
                  )}
                </div>
                {/* Reorder + edit row */}
                <div className="flex items-center gap-1.5 mt-3">
                  <div className="inline-flex rounded-md border-2 border-border overflow-hidden">
                    <button
                      onClick={() => void move(idx, -1)}
                      disabled={idx === 0}
                      className="w-9 h-9 inline-flex items-center justify-center text-gray-700 hover:bg-light disabled:opacity-30 disabled:cursor-not-allowed border-r border-border"
                      title="Move up"
                    >
                      <IconArrowUp size={14} stroke={2.25} />
                    </button>
                    <button
                      onClick={() => void move(idx, 1)}
                      disabled={idx === banners.length - 1}
                      className="w-9 h-9 inline-flex items-center justify-center text-gray-700 hover:bg-light disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <IconArrowDown size={14} stroke={2.25} />
                    </button>
                  </div>
                  {b.linkUrl && (
                    <a href={b.linkUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-md border-2 border-border text-gray-700 hover:border-primary hover:text-primary" title="Open link">
                      <IconLink size={14} stroke={2.25} />
                    </a>
                  )}
                  <button
                    onClick={() => void toggleActive(b)}
                    className={`w-9 h-9 inline-flex items-center justify-center rounded-md border-2 ${b.isActive ? 'border-danger text-danger hover:bg-danger hover:text-[#FFFFFF]' : 'border-success text-success hover:bg-success hover:text-[#FFFFFF]'}`}
                    title={b.isActive ? 'Deactivate' : 'Reactivate'}
                  >
                    {b.isActive ? <IconBan size={14} stroke={2.25} /> : <IconCircleCheck size={14} stroke={2.25} />}
                  </button>
                  <button
                    onClick={() => setEditing(b)}
                    className="flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-md border-2 border-border text-13 font-bold text-heading hover:border-primary hover:text-primary"
                  >
                    <IconPencil size={14} stroke={2.25} />
                    Edit
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <BannerFormModal
        target={editing}
        products={products}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={() => { void load(); setEditing(null); }}
      />
    </AdminLayout>
  );
}

function BannerFormModal({
  target, products, categories, onClose, onSaved,
}: {
  target: ApiBanner | 'new' | null;
  products: ApiProduct[];
  categories: ApiCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;
  const [imageUrl, setImageUrl] = useState('');
  const [title, setTitle]       = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [linkType, setLinkType] = useState<LinkType>('none');
  const [linkUrl, setLinkUrl]           = useState('');
  const [linkProductId, setLinkProductId]   = useState('');
  const [linkCategoryId, setLinkCategoryId] = useState('');
  const [sortOrder, setSortOrder] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr]           = useState<string | null>(null);

  async function uploadViaCloudinary() {
    setErr(null);
    setUploading(true);
    try {
      const url = await pickImageViaCloudinary({ folder: 'habby-pos/banners' });
      setImageUrl(url);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setImageUrl(''); setTitle(''); setSubtitle('');
      setLinkType('none'); setLinkUrl(''); setLinkProductId(''); setLinkCategoryId('');
      setSortOrder(100); setIsActive(true);
    } else {
      setImageUrl(target.imageUrl); setTitle(target.title ?? '');
      setSubtitle(target.subtitle ?? '');
      setLinkUrl(target.linkUrl ?? '');
      setLinkProductId(target.linkProductId ?? '');
      setLinkCategoryId(target.linkCategoryId ?? '');
      // Derive linkType from whichever target id/url is populated.
      setLinkType(
        target.linkProductId  ? 'product'  :
        target.linkCategoryId ? 'category' :
        target.linkUrl        ? 'url'      : 'none'
      );
      setSortOrder(target.sortOrder); setIsActive(target.isActive);
    }
    setErr(null);
  }, [target]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const url = isNew ? '/api/banners' : `/api/banners/${existing!.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          title: title.trim() || null,
          subtitle: subtitle.trim() || null,
          linkUrl:        linkType === 'url'      ? (linkUrl.trim() || null)    : null,
          linkProductId:  linkType === 'product'  ? (linkProductId || null)     : null,
          linkCategoryId: linkType === 'category' ? (linkCategoryId || null)    : null,
          sortOrder,
          isActive,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const productsByCategory = (() => {
    const m = new Map<string, ApiProduct[]>();
    for (const p of products) {
      if (!m.has(p.categoryId)) m.set(p.categoryId, []);
      m.get(p.categoryId)!.push(p);
    }
    return m;
  })();

  return (
    <Modal open={target !== null} onClose={onClose} size="md" title={isNew ? 'New banner' : 'Edit banner'}>
      <div className="space-y-4">
        <Field label="Image" required helper="Upload via Cloudinary (cropped to 1:1 square) — or paste an external URL">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => void uploadViaCloudinary()}
              disabled={uploading}
              className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              <IconCloudUpload size={18} stroke={2.25} />
              {uploading ? 'Opening uploader…' : (imageUrl ? 'Replace image' : 'Upload image')}
            </button>
            <p className="text-11 font-semibold uppercase tracking-widest text-gray-500 text-center">or paste a URL</p>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-13 font-mono text-heading"
            />
          </div>
        </Field>
        {imageUrl.trim() && (
          <div className="rounded-md bg-light border border-border-soft p-2">
            <p className="text-11 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Preview</p>
            <div className="aspect-square w-full rounded overflow-hidden bg-surface">
              <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
            </div>
          </div>
        )}
        <Field label="Title" helper="Optional — shown over the image">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Weekend special"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
          />
        </Field>
        <Field label="Subtitle" helper="Optional — small caption under the title">
          <input
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="e.g. 20% off all milkshakes this Sat–Sun"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
          />
        </Field>
        <Field label="Tap action" helper="What happens when a customer taps this banner">
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-1.5">
              {([
                { v: 'none',     label: 'None' },
                { v: 'product',  label: 'Product' },
                { v: 'category', label: 'Category' },
                { v: 'url',      label: 'URL' },
              ] as const).map((opt) => {
                const active = linkType === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setLinkType(opt.v)}
                    className={[
                      'h-10 rounded-md border-2 text-13 font-bold transition-colors',
                      active
                        ? 'bg-primary border-primary text-[#FFFFFF]'
                        : 'bg-surface border-border text-heading hover:border-primary',
                    ].join(' ')}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {linkType === 'product' && (
              <select
                value={linkProductId}
                onChange={(e) => setLinkProductId(e.target.value)}
                className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
              >
                <option value="">— pick a product —</option>
                {categories.map((c) => {
                  const prods = productsByCategory.get(c.id) ?? [];
                  if (prods.length === 0) return null;
                  return (
                    <optgroup key={c.id} label={c.name}>
                      {prods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
            )}
            {linkType === 'category' && (
              <select
                value={linkCategoryId}
                onChange={(e) => setLinkCategoryId(e.target.value)}
                className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
              >
                <option value="">— pick a category —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {linkType === 'url' && (
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://…"
                className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-mono text-heading"
              />
            )}
          </div>
        </Field>
        <Field label="Sort order" helper="Lower = shown first">
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading"
          />
        </Field>
        <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md bg-light border border-border-soft">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4" />
          <span>
            <span className="text-14 font-bold text-heading block">Active</span>
            <span className="text-12 font-medium text-gray-700 block">Shown to customers in the carousel</span>
          </span>
        </label>

        {err && <p className="text-13 font-semibold text-danger">{err}</p>}

        <div className="flex items-stretch gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving || !imageUrl.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300">
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : (isNew ? 'Create banner' : 'Save')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, helper, required, children }: { label: string; helper?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}{required && <span className="text-danger ml-1">*</span>}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      {children}
    </label>
  );
}
